import {
  alpha,
  Theme,
  useTheme,
  experimentalStyled as styled
} from '@material-ui/core/styles';
import { ButtonGroup, ButtonGroupProps } from '@material-ui/core';
// @types
import { ColorSchema } from '../../@types/theme';

// ----------------------------------------------------------------------

const ButtonGroupStyle = styled(ButtonGroup)(
  ({
    theme,
    styleProps
  }: {
    theme: Theme;
    styleProps: {
      color: ColorSchema;
      variant: 'contained' | 'outlined' | 'text';
    };
  }) => {
    const { color, variant } = styleProps;

    const styleContained = (colorSchema: ColorSchema) => ({
      boxShadow: theme.customShadows[colorSchema],
      '& .MuiButtonGroup-grouped': {
        color: theme.palette[colorSchema].contrastText,
        backgroundColor: theme.palette[colorSchema].main,
        borderColor: `${theme.palette[colorSchema].dark} !important`,
        '&:hover': {
          backgroundColor: theme.palette[colorSchema].dark
        }
      }
    });

    const styleOutlined = (colorSchema: ColorSchema) => ({
      '& .MuiButtonGroup-grouped': {
        color: theme.palette[colorSchema].main,
        borderColor: `${alpha(
          theme.palette[colorSchema].main,
          0.48
        )} !important`,
        '&:hover': {
          borderColor: `${theme.palette[colorSchema].main} !important`,
          backgroundColor: alpha(
            theme.palette[colorSchema].main,
            theme.palette.action.hoverOpacity
          )
        }
      }
    });

    const styleText = (colorSchema: ColorSchema) => ({
      '& .MuiButtonGroup-grouped': {
        color: theme.palette[colorSchema].main,
        borderColor: `${theme.palette[colorSchema].main} !important`,
        '&:hover': {
          backgroundColor: alpha(
            theme.palette[colorSchema].main,
            theme.palette.action.hoverOpacity
          )
        }
      }
    });
    return {
      ...(variant === 'contained' && { ...styleContained(color) }),
      ...(variant === 'outlined' && { ...styleOutlined(color) }),
      ...(variant === 'text' && { ...styleText(color) })
    };
  }
);

// ----------------------------------------------------------------------

interface MButtonGroupProps extends Omit<ButtonGroupProps, 'color'> {
  color?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error';
}

export default function MButtonGroup({
  color = 'primary',
  variant = 'outlined',
  children,
  ...other
}: MButtonGroupProps) {
  const theme = useTheme();

  if (color === 'inherit' || color === 'primary' || color === 'secondary') {
    return (
      <ButtonGroup color={color} variant={variant} {...other}>
        {children}
      </ButtonGroup>
    );
  }

  return (
    <ButtonGroupStyle
      variant={variant}
      styleProps={{ color, variant }}
      theme={theme}
      {...other}
    >
      {children}
    </ButtonGroupStyle>
  );
}
